import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  Search,
  Mail,
  Phone,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Settings,
  Info,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import type { PromiseToPay } from '@/types/collections';
import { Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { PromiseDetailsDrawer } from '@/components/collections/promise-details-drawer';
import { CreatePromiseDrawer, type PromiseFormData } from '@/components/collections/create-promise-drawer';
import { CaseDetailsDialog } from '@/components/collections/case-details-dialog';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import type { CollectionCase } from '@/types/collections';
import { ColumnConfigDialog } from '@/components/collections/column-config-dialog';
import { usePromiseColumnPreferences } from '@/hooks/usePromiseColumnPreferences';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { exportPromisesToExcel } from '@/lib/promise-export-utils';
import { formatCurrency } from '@/lib/utils/formatters';
import { HelpSupportButton } from '@/components/help-support';
import { usePromisesList, useCreatePromise, type CreatePromiseRequest } from '@/hooks/usePromisesData';

const PromisesPage: React.FC = () => {
  // Fetch promises using mode-aware hook (demo/live)
  const { data: promisesData, isLoading: promisesLoading } = usePromisesList();
  const promises = promisesData?.promises ?? [];
  
  // Create promise mutation (mode-aware - calls API in live mode, returns mock in demo mode)
  const createPromiseMutation = useCreatePromise();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [metricFilter, setMetricFilter] = useState<'all' | 'active' | 'broken' | 'dueThisWeek'>('all');
  const [selectedPromise, setSelectedPromise] = useState<PromiseToPay | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [selectedPromiseIds, setSelectedPromiseIds] = useState<Set<string>>(new Set());
  const [selectedCaseForDetails, setSelectedCaseForDetails] = useState<CollectionCase | null>(null);
  const [caseDetailsOpen, setCaseDetailsOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Column preferences
  const { columns, savePreferences, resetToDefaults } = usePromiseColumnPreferences();

  // Auto-update promises with past promise dates to 'Broken' status
  const processedPromises = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    return promises.map(promise => {
      const promiseDate = new Date(promise.promiseDate);
      promiseDate.setHours(0, 0, 0, 0); // Reset time to start of day

      // If promise date is in the past and status is 'Active', mark as 'Broken'
      if (promiseDate < today && promise.status === 'Active') {
        const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
        return {
          ...promise,
          status: 'Broken' as const,
          daysOverdue,
        };
      }

      return promise;
    });
  }, [promises]);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const active = processedPromises.filter(p => p.status === 'Active');
    const broken = processedPromises.filter(p => p.status === 'Broken');
    const dueThisWeek = processedPromises.filter(p => {
      const daysUntilDue = Math.ceil((p.promiseDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilDue >= 0 && daysUntilDue <= 7 && p.status === 'Active';
    });

    return {
      activePromises: {
        count: active.length,
        totalValue: active.reduce((sum, p) => sum + p.promisedAmount, 0),
      },
      brokenPromises: {
        count: broken.length,
        totalValue: broken.reduce((sum, p) => sum + p.promisedAmount, 0),
      },
      dueThisWeek: {
        count: dueThisWeek.length,
        totalValue: dueThisWeek.reduce((sum, p) => sum + p.promisedAmount, 0),
      },
    };
  }, [processedPromises]);

  // Filter and sort promises based on status filter and search
  const filteredPromises = useMemo(() => {
    let filtered = processedPromises;

    // Filter by metric card selection
    if (metricFilter === 'active') {
      filtered = filtered.filter(p => p.status === 'Active');
    } else if (metricFilter === 'broken') {
      filtered = filtered.filter(p => p.status === 'Broken');
    } else if (metricFilter === 'dueThisWeek') {
      filtered = filtered.filter(p => {
        const daysUntilDue = Math.ceil((p.promiseDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilDue >= 0 && daysUntilDue <= 7 && p.status === 'Active';
      });
    }

    // Filter by status dropdown
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.customerName.toLowerCase().includes(query) ||
        p.customerAccountNumber.toLowerCase().includes(query) ||
        p.promisedAmount.toString().includes(query)
      );
    }

    // Sort by Promise Date in ascending order (oldest first)
    filtered = [...filtered].sort((a, b) => a.promiseDate.getTime() - b.promiseDate.getTime());

    return filtered;
  }, [processedPromises, statusFilter, searchQuery, metricFilter]);

  const handleViewDetails = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setDetailSheetOpen(true);
  };

  const handleEmailCustomer = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setEmailModalOpen(true);
  };

  const handleLogCall = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setCallModalOpen(true);
  };

  const handleCreateTask = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setTaskModalOpen(true);
  };

  const handleCaseNumberClick = (caseNumber: string) => {
    // Get the case from the global data store
    const dataStore = getGlobalDataStore();
    const allCases = dataStore.getAllCases();
    const caseData = allCases.find(c => c.caseId === caseNumber);
    
    if (caseData) {
      setSelectedCaseForDetails(caseData);
      setCaseDetailsOpen(true);
    } else {
      toast.error('Case not found');
    }
  };

  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { variant: 'default' | 'destructive' | 'secondary' | 'outline'; className: string }> = {
      Active: { variant: 'default', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
      Broken: { variant: 'destructive', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
      Paid: { variant: 'default', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
      'Partially Paid': { variant: 'secondary', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
    };
    return <Badge className={variants[status].className}>{status}</Badge>;
  };

  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3 mr-1" />,
      Email: <Mail className="h-3 w-3 mr-1" />,
      Portal: <TrendingUp className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  const handleCreateNewPromise = (data: PromiseFormData) => {
    // Map form data to API request format
    const request: CreatePromiseRequest = {
      customerId: parseInt(data.customer.id, 10),
      promiseDate: format(data.promiseDate, 'yyyy-MM-dd'),
      promisedAmount: data.promisedAmount,
      confirmationMethod: data.confirmationMethod.toLowerCase(),
      notes: data.notes,
    };

    createPromiseMutation.mutate(request, {
      onSuccess: () => {
        toast.success('New promise created successfully');
        setCreatePromiseModalOpen(false);
      },
      onError: (error) => {
        toast.error(`Failed to create promise: ${error.message}`);
      },
    });
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredPromises.map(p => p.id));
      setSelectedPromiseIds(allIds);
    } else {
      setSelectedPromiseIds(new Set());
    }
  };

  const handleSelectPromise = (promiseId: string, checked: boolean) => {
    const newSelection = new Set(selectedPromiseIds);
    if (checked) {
      newSelection.add(promiseId);
    } else {
      newSelection.delete(promiseId);
    }
    setSelectedPromiseIds(newSelection);
  };

  const isAllSelected = filteredPromises.length > 0 && selectedPromiseIds.size === filteredPromises.length;
  const isSomeSelected = selectedPromiseIds.size > 0 && selectedPromiseIds.size < filteredPromises.length;

  // Bulk action handlers
  const handleBulkViewDetails = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = processedPromises.find(p => p.id === promiseId);
      if (promise) {
        handleViewDetails(promise);
      }
    } else {
      toast.info(`Viewing details for ${selectedPromiseIds.size} selected promises`);
    }
  };

  const handleBulkEmail = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = processedPromises.find(p => p.id === promiseId);
      if (promise) {
        handleEmailCustomer(promise);
      }
    } else {
      toast.success(`Email sent to ${selectedPromiseIds.size} customers`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleBulkCall = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = processedPromises.find(p => p.id === promiseId);
      if (promise) {
        handleLogCall(promise);
      }
    } else {
      toast.info(`Logging calls for ${selectedPromiseIds.size} customers`);
    }
  };

  const handleBulkTask = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = processedPromises.find(p => p.id === promiseId);
      if (promise) {
        handleCreateTask(promise);
      }
    } else {
      toast.success(`Created follow-up tasks for ${selectedPromiseIds.size} promises`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleClearSelection = () => {
    setSelectedPromiseIds(new Set());
  };

  // Metric card click handlers
  const handleMetricCardClick = (filter: 'all' | 'active' | 'broken' | 'dueThisWeek') => {
    if (metricFilter === filter) {
      // If clicking the same filter, clear it
      setMetricFilter('all');
      setStatusFilter('all');
    } else {
      // Set the new filter
      setMetricFilter(filter);
      // Also update status filter for consistency
      if (filter === 'active') {
        setStatusFilter('Active');
      } else if (filter === 'broken') {
        setStatusFilter('Broken');
      } else if (filter === 'dueThisWeek') {
        setStatusFilter('Active');
      } else {
        setStatusFilter('all');
      }
    }
  };

  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);

  // Export handler - current view
  const handleExportToExcel = () => {
    try {
      exportPromisesToExcel(filteredPromises);
      toast.success(`Exported ${filteredPromises.length} promise(s) to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  };

  // Export handler - all columns
  const handleExportAllColumns = () => {
    try {
      exportPromisesToExcel(filteredPromises, `promises-all-columns-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`Exported ${filteredPromises.length} promise(s) with all columns to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  };

  // Helper function to render table cell content based on column ID
  const renderTableCell = (columnId: string, promise: PromiseToPay) => {
    switch (columnId) {
      case 'select':
        return (
          <Checkbox
            checked={selectedPromiseIds.has(promise.id)}
            onCheckedChange={(checked) => handleSelectPromise(promise.id, checked as boolean)}
            aria-label={`Select promise ${promise.id}`}
          />
        );
      case 'promiseId':
        return (
          <button
            onClick={() => handleViewDetails(promise)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors"
          >
            {promise.id}
          </button>
        );
      case 'customerName':
        return (
          <button
            onClick={() => handleViewDetails(promise)}
            className="text-left hover:underline text-primary font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          >
            {promise.customerName}
          </button>
        );
      case 'customerAccountNumber':
        return <span>{promise.customerAccountNumber}</span>;
      case 'caseNumber':
        return promise.caseNumber ? (
          <button
            onClick={() => handleCaseNumberClick(promise.caseNumber)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
          >
            {promise.caseNumber}
          </button>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'lastAction':
        return promise.lastAction ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-sm capitalize">{promise.lastAction.type.replace('_', ' ')}</p>
                  <p className="text-xs">{promise.lastAction.description}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                    <span>{promise.lastAction.performedBy}</span>
                    <span>{format(promise.lastAction.date, 'dd/MM/yyyy')}</span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'promiseDate':
        return <span>{format(promise.promiseDate, 'dd/MM/yyyy')}</span>;
      case 'daysOverdue':
        return promise.daysOverdue && promise.daysOverdue > 0 ? (
          <span className="text-red-600 font-medium">{promise.daysOverdue}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'amount':
        return <div className="font-medium">{formatCurrency(promise.promisedAmount)}</div>;
      case 'transactions':
        return <div className="text-sm">{promise.invoices.length}</div>;
      case 'status':
        return getStatusBadge(promise.status);
      case 'method':
        return getConfirmationMethodBadge(promise.confirmationMethod);
      case 'customerEmail':
        return <span className="text-sm">{promise.customerEmail || '-'}</span>;
      case 'customerPhone':
        return <span className="text-sm">{promise.customerPhone || '-'}</span>;
      case 'createdDate':
        return <span className="text-sm">{promise.createdAt ? format(new Date(promise.createdAt), 'dd/MM/yyyy') : '-'}</span>;
      case 'createdBy':
        return <span className="text-sm">{promise.createdBy || '-'}</span>;
      case 'notes':
        return <span className="text-sm truncate max-w-xs">{promise.notes || '-'}</span>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins className="h-8 w-8" />
            Promise to Pay Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Track customer payment commitments and monitor promise fulfillment
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCreatePromiseModalOpen(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create New Promise
          </Button>
          <HelpSupportButton screenId="promises" />
        </div>
      </div>

      {/* Loading State */}
      {promisesLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading promises...</span>
        </div>
      )}

      {/* KPI Cards */}
      {!promisesLoading && <div className="grid gap-4 md:grid-cols-3">
        <Card 
          className={`border-l-4 border-l-green-500 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'active' ? 'ring-2 ring-green-500 shadow-md' : ''
          }`}
          onClick={() => handleMetricCardClick('active')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Promises</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.activePromises.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(kpis.activePromises.totalValue)} total value
            </p>
            {metricFilter === 'active' && (
              <p className="text-xs text-green-600 font-medium mt-1">
                ✓ Filtering active promises
              </p>
            )}
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-red-500 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'broken' ? 'ring-2 ring-red-500 shadow-md' : ''
          }`}
          onClick={() => handleMetricCardClick('broken')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Broken Promises</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.brokenPromises.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(kpis.brokenPromises.totalValue)} at risk
            </p>
            {metricFilter === 'broken' && (
              <p className="text-xs text-red-600 font-medium mt-1">
                ✓ Filtering broken promises
              </p>
            )}
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-amber-500 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'dueThisWeek' ? 'ring-2 ring-amber-500 shadow-md' : ''
          }`}
          onClick={() => handleMetricCardClick('dueThisWeek')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due This Week</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.dueThisWeek.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(kpis.dueThisWeek.totalValue)} expected
            </p>
            {metricFilter === 'dueThisWeek' && (
              <p className="text-xs text-amber-600 font-medium mt-1">
                ✓ Filtering promises due this week
              </p>
            )}
          </CardContent>
        </Card>
      </div>}

      {/* Promised Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Promises ({filteredPromises.length})</CardTitle>
              <CardDescription>
                All payment promises sorted by promise date (oldest first)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Configure Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        
        {/* Search Bar and Status Filter */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by customer name, account number, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select 
              value={statusFilter} 
              onValueChange={(value) => {
                setStatusFilter(value);
                // Clear metric filter when manually changing status dropdown
                setMetricFilter('all');
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Broken">Broken</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Partially Paid">Partially Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((column) => (
                      <TableHead 
                        key={column.id}
                        className={column.id === 'select' ? 'w-12' : ''}
                      >
                        {column.id === 'select' ? (
                          <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={handleSelectAll}
                            aria-label="Select all promises"
                            className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                          />
                        ) : (
                          column.label
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPromises.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} className="text-center text-muted-foreground py-8">
                        No promises found matching your criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPromises.map((promise) => {
                      const isSelected = selectedPromiseIds.has(promise.id);
                      return (
                        <TableRow 
                          key={promise.id}
                          className={isSelected ? "bg-muted/50" : ""}
                        >
                          {visibleColumns.map((column) => (
                            <TableCell key={column.id}>
                              {renderTableCell(column.id, promise)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
        </CardContent>
      </Card>

      {/* Promise Details Drawer */}
      <PromiseDetailsDrawer
        promise={selectedPromise}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Customer</DialogTitle>
            <DialogDescription>
              Send a reminder email to {selectedPromise?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input value={selectedPromise?.customerEmail || ''} disabled />
            </div>
            <div>
              <Label>Subject</Label>
              <Input placeholder="Payment Reminder" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea placeholder="Enter your message..." rows={6} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Email sent successfully');
              setEmailModalOpen(false);
            }}>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Log Modal */}
      <Dialog open={callModalOpen} onOpenChange={setCallModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>
              Record call details with {selectedPromise?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Call Outcome</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="promise-confirmed">Promise Confirmed</SelectItem>
                  <SelectItem value="promise-broken">Promise Broken</SelectItem>
                  <SelectItem value="no-answer">No Answer</SelectItem>
                  <SelectItem value="voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="dispute">Dispute Raised</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="Call notes..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Call logged successfully');
              setCallModalOpen(false);
            }}>
              Save Call Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Follow-up Task</DialogTitle>
            <DialogDescription>
              Schedule a follow-up action for {selectedPromise?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Task Type</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Follow-up Call</SelectItem>
                  <SelectItem value="email">Send Email</SelectItem>
                  <SelectItem value="review">Review Promise</SelectItem>
                  <SelectItem value="escalate">Escalate Case</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="Task details..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Task created successfully');
              setTaskModalOpen(false);
            }}>
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Promise Drawer */}
      <CreatePromiseDrawer
        open={createPromiseModalOpen}
        onOpenChange={setCreatePromiseModalOpen}
        onSave={handleCreateNewPromise}
      />

      {/* Case Details Drawer */}
      <CaseDetailsDialog
        open={caseDetailsOpen}
        onOpenChange={setCaseDetailsOpen}
        caseData={selectedCaseForDetails}
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Floating Actions Bar */}
      {selectedPromiseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedPromiseIds.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedPromiseIds.size === 1 ? "promise" : "promises"} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkEmail}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkCall}
                  className="gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Log Call
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromisesPage;
